import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Article as ArticleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { usePublicArticles } from '../../hooks/usePublicArticles';
import {
  BLOG_CATEGORY_LABELS,
  formatArticleDate,
  type BlogArticle,
} from '../../constants/blogArticles';
import MarketingHeading from '../shared/MarketingHeading';

interface ArticleCardProps {
  article: BlogArticle;
  compact?: boolean;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({ article, compact = false }) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: alpha(theme.palette.divider, 0.9),
        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: theme.shadows[3],
          transform: 'translateY(-2px)',
        },
      }}
    >
      {article.coverImageUrl ? (
        <Box
          component="img"
          src={article.coverImageUrl}
          alt={article.title}
          sx={{
            width: '100%',
            height: compact ? 140 : 160,
            objectFit: 'cover',
            display: 'block',
            bgcolor: 'action.hover',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: compact ? 100 : 120,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          }}
        >
          <ArticleIcon sx={{ fontSize: 40 }} />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={BLOG_CATEGORY_LABELS[article.category] || article.category}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          {article.featured ? (
            <Chip size="small" color="primary" label="Featured" sx={{ fontWeight: 600 }} />
          ) : null}
        </Stack>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
          {article.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.65,
            flexGrow: 1,
            display: '-webkit-box',
            WebkitLineClamp: compact ? 3 : 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.excerpt}
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {formatArticleDate(article.publishedAt || article.createdAt)}
          </Typography>
          <Button
            component={RouterLink}
            to={`/insights/${article.slug}`}
            size="small"
            endIcon={<ArrowForwardIcon />}
          >
            Read
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

interface ArticlesTeaserSectionProps {
  id?: string;
  limit?: number;
}

const ArticlesTeaserSection: React.FC<ArticlesTeaserSectionProps> = ({
  id = 'insights',
  limit = 3,
}) => {
  const theme = useTheme();
  const { articles, loading, error } = usePublicArticles({
    featured: true,
    limit,
    enabled: true,
  });

  if (!loading && !error && articles.length === 0) {
    return null;
  }

  return (
    <Box id={id} sx={{ scrollMarginTop: 96 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          <ArticleIcon />
        </Box>
        <MarketingHeading level="h3">
          Insights &amp; articles
        </MarketingHeading>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720, lineHeight: 1.7 }}>
        Practical guidance on carbon measurement, compliance reporting, and MSME sustainability from the Sustainow team.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Typography variant="body2" color="text.secondary">
          Insights are temporarily unavailable. Please check back soon.
        </Typography>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            {articles.map((article) => (
              <Grid item xs={12} md={4} key={article.id}>
                <ArticleCard article={article} compact />
              </Grid>
            ))}
          </Grid>
          <Button
            component={RouterLink}
            to="/insights"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
          >
            View all insights
          </Button>
        </>
      )}
    </Box>
  );
};

export default ArticlesTeaserSection;
